export const privacyLegalContent = String.raw`
<p class="public-info-effective">Effective Date: August 13, 2026</p>

<p>Liminl Inc. ("we," "us," "our," or "Liminl") operates the Liminl website and web application (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use the Service. By using the Service, you consent to the practices described in this Privacy Policy. If you do not agree with this policy, please do not use the Service.</p>

<section id="who-we-are">
  <h2>Who We Are</h2>
  <p>Liminl is operated by Liminl Inc., a Delaware corporation. For the purposes of GDPR and similar laws, we are the data controller for the information described in this policy.</p>
  <p>Registered Address: San Francisco, CA<br />
    Contact: support@goliminl.com<br />
    Web: www.goliminl.com</p>
  <p>If you have questions, complaints, or want to exercise any of the rights described below, write to the contact address above. We respond within 30 days.</p>
</section>

<section id="information-we-collect">
  <h2>1. Information We Collect</h2>
  <h3>1.1 Information You Provide Directly</h3>
  <p>Depending on how you use the Service, this may include:</p>
  <ul>
    <li>Account information for students and other signed-in users: email address, name, and password (stored securely; we never store plaintext passwords)</li>
    <li>Onboarding information: career goals, education, work history, skills, target roles, companies, locations, work authorization, and other answers you provide in chat or review</li>
    <li>Resume files you upload, and LinkedIn profile URLs or screenshots you choose to submit during setup</li>
    <li>Alumni opt-in information if you use the alumni form: name, email, graduation year, company, title, industry, seniority, city, optional LinkedIn URL, optional scheduling link, conversation capacity, and notes</li>
    <li>Matching and scheduling information: intro requests, offered times, selected meeting times, time zone, and post-conversation feedback</li>
    <li>Student references: names and email addresses you provide so we can ask those people to confirm they know you, plus their Yes or No response</li>
  </ul>
  <h3>1.2 Information Collected Automatically</h3>
  <ul>
    <li>Device and browser information (type, operating system, browser version)</li>
    <li>IP address and approximate location (city-level, derived from IP)</li>
    <li>Usage data (pages visited within Liminl, features used, session duration)</li>
    <li>Cookies and similar technologies needed to keep you signed in and to operate the Service (see Section 7)</li>
  </ul>
  <h3>1.3 Information from Google Sign-In</h3>
  <p>If you choose to sign in with Google, we receive basic identity information from Google Identity Services, such as your name, email address, and profile image. We use this only to create or sign in to your Liminl account.</p>
  <p>We do not request access to Gmail, Google Calendar, or Google Contacts. We cannot read, send, or draft email in your Google account, and we do not read your personal calendar or address book.</p>
  <h3>1.4 Information from Third-Party Sources Used for Matching</h3>
  <p>To prepare introductions and related emails, we may supplement information you provide with publicly available professional data (for example, title, employer, or education) and with public web research used to draft match context. We use these sources only to operate matching and related communications. We do not sell enriched data.</p>
</section>

<section id="what-we-do-not-collect">
  <h2>2. What We Do Not Collect</h2>
  <ul>
    <li>Gmail or other inbox content (we have no Gmail access)</li>
    <li>Your Google Calendar events or Google Contacts</li>
    <li>Phone numbers, unless you explicitly enter one</li>
    <li>Financial or payment data (we do not process payments directly)</li>
    <li>Precise geolocation (no GPS or device-proximity data)</li>
    <li>Your browsing history or activity outside the Liminl website and web application</li>
  </ul>
</section>

<section id="how-we-use-information">
  <h2>3. How We Use Your Information</h2>
  <p>We use your information for the following purposes:</p>
  <ul>
    <li>To create and maintain your account, verify your email, and keep you signed in</li>
    <li>To complete onboarding and understand your background and goals</li>
    <li>To collect student references and email those people from Liminl so they can confirm they know you</li>
    <li>To match students with alumni and show match context inside the Service</li>
    <li>To schedule introduction conversations, including creating a Liminl-hosted calendar event and a video meeting link</li>
    <li>To send Service emails from Liminl (for example, reference confirmation requests, match notices, scheduling, preparation, and follow-up). Reference confirmation emails use a fixed template and are not personalized with outside research</li>
    <li>To collect optional feedback after a conversation</li>
    <li>To detect, prevent, and address technical issues, security threats, and fraud, including error monitoring</li>
    <li>To analyze aggregate, anonymous usage patterns and improve the Service</li>
    <li>To comply with legal obligations</li>
  </ul>
  <p>Liminl sends its own emails about matching, scheduling, and student references. We do not send messages from your personal email account. If you name someone as a reference, you confirm you have a legitimate reason to share their name and email with us for this purpose, and that we may email them from Liminl.</p>
</section>

<section id="ai-powered-features">
  <h2>4. AI-Powered Features</h2>
  <p>Liminl uses artificial intelligence to extract information from resumes or LinkedIn materials you submit, to help complete onboarding, to generate match explanations, and to draft personalized introduction and follow-up emails.</p>
  <p>We currently use the Anthropic API for these features. Data sent to this API is subject to Anthropic's data processing terms and is used only to provide the Service.</p>
  <p>AI-generated content consists of suggestions and drafts. Where an email is sent by Liminl, it is sent from Liminl, not from your inbox. We do not use your personal data, including Google sign-in data, to train general-purpose AI models.</p>
</section>

<section id="how-we-share-information">
  <h2>5. How We Share Your Information</h2>
  <p>We do not sell your personal information. We share data only in the following limited circumstances:</p>
  <p><strong>Service Providers.</strong> We share data with vendors who help us operate the Service, including cloud hosting, email delivery, AI processing, error monitoring, location lookup, video meetings, and public professional-data or web-research providers used for matching emails. These providers are used only for the purposes we specify.</p>
  <p><strong>Matched Participants.</strong> If you are matched for an introduction, we share the professional information needed to make that introduction (such as name, school or company, title, and why you were matched) with the other participant and in related Liminl emails.</p>
  <p><strong>Student references.</strong> If you submit names and emails as references, we email those people from Liminl and show them your name so they can confirm whether they know you. We do not show them your email address or your other references.</p>
  <p><strong>Legal Requirements.</strong> We may disclose your information if required by law, legal process, or government request, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</p>
  <p><strong>Business Transfers.</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and obtain explicit prior consent where required.</p>
  <p>We do not sell or share your data with advertisers, data brokers, or any party not described above.</p>
</section>

<section id="legal-basis">
  <h2>6. Legal Basis for Processing (GDPR)</h2>
  <p>If you are in the European Economic Area, the United Kingdom, or Switzerland, we process your personal data on the following lawful bases: performance of our contract with you (account creation, onboarding, matching, scheduling, and related emails), your consent (Google sign-in, optional alumni form fields, and optional uploads), and our legitimate interests (security logging, fraud prevention, and error monitoring). You may withdraw consent by disconnecting Google sign-in where available or by requesting account deletion.</p>
  <p>We do not use automated processing to make decisions that produce legal effects or similarly significantly affect you. Match suggestions are informational and require human action (such as requesting an intro or choosing a time).</p>
</section>

<section id="cookies">
  <h2>7. Cookies and Similar Technologies</h2>
  <p>The Liminl web app uses cookies and similar technologies for authentication, security, and error monitoring. These are used to operate the Service. We do not use cookies for advertising, retargeting, or third-party advertising analytics. You can manage cookie preferences through your browser settings; disabling essential cookies may affect the functionality of the Service.</p>
</section>

<section id="data-security">
  <h2>8. Data Storage and Security</h2>
  <p>Your data is stored in the United States, encrypted in transit and at rest. Matching, scheduling, and related operational records may be stored in our cloud systems and operations tools used to run the Service. Access to production systems is limited to authorized Liminl personnel.</p>
  <p>We implement reasonable technical and organizational measures to protect your personal information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
  <p>If we discover a breach affecting your personal data, we will notify you and any relevant authorities as required by applicable law, including GDPR and applicable US state breach-notification laws. If you become aware of a security vulnerability, please report it to support@goliminl.com.</p>
</section>

<section id="international-transfers">
  <h2>9. International Data Transfers</h2>
  <p>Liminl is operated from and stores data in the United States. If you access the Service from outside the United States, your information will be transferred to and processed in the United States. For transfers from the EEA, UK, or Switzerland, we rely on your explicit consent or Standard Contractual Clauses with our infrastructure providers.</p>
</section>

<section id="data-retention">
  <h2>10. Data Retention</h2>
  <p>We retain your personal information for as long as your account is active, or as needed to provide matching and scheduling if you submitted an alumni form without an account. When you request deletion, your data is removed from our primary systems promptly and from backups within 30 days. Anonymized and aggregated data that cannot identify you may be retained for analytics and product improvement.</p>
</section>

<section id="your-rights">
  <h2>11. Your Rights and Choices</h2>
  <p>You have the following rights over your personal data:</p>
  <ul>
    <li><strong>Access:</strong> request a copy of the personal information we hold about you.</li>
    <li><strong>Correction:</strong> request that we correct inaccurate or incomplete information.</li>
    <li><strong>Deletion:</strong> request that we delete your personal information or your entire account and all data permanently.</li>
    <li><strong>Restriction and Objection:</strong> request that we pause or stop certain processing.</li>
    <li><strong>Data Portability:</strong> request a copy of your data in a structured, machine-readable format.</li>
    <li><strong>Opt-Out of Enrichment:</strong> request that we stop supplementing your matching profile with third-party professional data.</li>
    <li><strong>Right to Lodge a Complaint:</strong> if you are in the EEA, UK, or Switzerland, you may lodge a complaint with your local data protection authority.</li>
  </ul>
  <p>To exercise any of these rights, contact us at support@goliminl.com. We will respond within 30 days.</p>
</section>

<section id="california-rights">
  <h2>12. California Residents (CCPA / CPRA)</h2>
  <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act and the California Privacy Rights Act.</p>
  <p>Categories of personal information we collect: identifiers (name, email address); authentication information (password; Google sign-in identifiers if you use Google to sign in); professional or employment-related information (onboarding answers, resume or LinkedIn materials you submit, alumni profile fields, match and scheduling details); names and emails you provide as student references, and the reference responses we receive; and inferences used to suggest matches and draft related emails.</p>
  <p>You have the right to know what we collect and why, the right to delete, the right to correct, and the right to non-discrimination for exercising these rights. We do not sell or share your personal information as defined under the CCPA, and we do not use sensitive personal information for purposes beyond providing the Service. To submit a request, contact support@goliminl.com from the email address associated with your account or alumni submission.</p>
</section>

<section id="childrens-privacy">
  <h2>13. Children's Privacy</h2>
  <p>The Service is not directed to individuals under the age of 18, and we do not knowingly collect personal information from anyone under 18. If you believe someone under 18 has created a Liminl account or submitted an alumni form, please contact us at support@goliminl.com and we will delete the information.</p>
</section>

<section id="policy-changes">
  <h2>14. Changes to This Privacy Policy</h2>
  <p>We may update this Privacy Policy from time to time. We will update the Effective Date at the top when we revise this document, and we will announce substantive changes by email before they take effect. If we later ask to access additional Google data (such as Calendar or Contacts), we will update this policy and ask for your consent before using that data.</p>
</section>

<section id="privacy-contact">
  <h2>15. Contact Us</h2>
  <p>Liminl Inc.<br />
    Email: support@goliminl.com<br />
    Web: www.goliminl.com</p>
</section>
`;

export const termsLegalContent = String.raw`
<p class="public-info-effective">Effective Date: August 13, 2026</p>

<section id="acceptance-of-terms">
  <h2>1. Acceptance of Terms</h2>
  <p>
    By accessing or using the Liminl platform (the "Service"), operated by Liminl Inc., a
    Delaware corporation, you agree to be bound by these Terms of Service ("Terms"). If you do not
    agree to these Terms, do not use the Service. We may update these Terms from time to time.
    Continued use of the Service after changes are posted constitutes acceptance of the updated
    Terms. We will notify you of material changes via email or an in-app notification.
  </p>
</section>

<section>
  <h2>2. Eligibility</h2>
  <p>
    You must be at least 18 years old to use Liminl. By using the Service, you represent that you
    meet this requirement and that the information you provide is accurate and complete.
  </p>
</section>

<section>
  <h2>3. Account Registration</h2>
  <p>
    Students and other signed-in users create an account with a valid email address and password, or
    by using Google sign-in. Alumni may submit an opt-in form without creating an account. You are
    responsible for maintaining the confidentiality of any account credentials and for all activity
    under your account.
  </p>
  <p>You agree to notify us immediately at support@goliminl.com if you become aware of any unauthorized use of your account.</p>
</section>

<section id="description-of-service">
  <h2>4. Description of Service</h2>
  <p>
    Liminl currently operates an introduction and matching service. The Service helps eligible
    students get matched with alumni for conversations, complete onboarding, request introductions,
    schedule meetings, and receive related emails from Liminl.
  </p>
  <h3>Student references</h3>
  <p>
    After onboarding, signed-in students name four references. Liminl emails those people from Liminl
    (not from the student's inbox) so they can confirm they know the student. This is attestation, not a
    background check. Submitting four valid references is required to use Matches. Verification replies
    do not block Matches. You must have a legitimate reason to share each person's name and email with
    Liminl for this purpose.
  </p>
  <h3>Matching and Scheduling</h3>
  <p>
    After submitting references, signed-in users can view matches, request introductions, and schedule times.
    Alumni who opt in may receive introduction requests and offer times. Liminl may create a
    Liminl-hosted calendar event and video meeting link for a confirmed conversation.
  </p>
  <h3>AI-Generated Content</h3>
  <p>
    Liminl uses artificial intelligence to help extract onboarding information, explain matches, and
    draft emails that Liminl sends about introductions. These outputs are generated algorithmically
    and should not be treated as professional career advice.
  </p>
  <h3>Google Sign-In</h3>
  <p>
    You may optionally sign in with Google. That sign-in provides basic identity information only.
    Liminl does not request or receive Gmail, Google Calendar, or Google Contacts access.
  </p>
</section>

<section id="user-data-and-privacy">
  <h2>5. User Data and Privacy</h2>
  <p>
    Your use of the Service is also governed by our Privacy Policy, which describes how we collect,
    use, and protect your information. By using the Service, you consent to the data practices
    described in the Privacy Policy.
  </p>
  <p>
    You retain ownership of the data you provide to Liminl. You grant us a limited, non-exclusive
    license to use, process, and display your data solely for the purpose of operating and improving
    the Service, including matching you with another participant and sending related Liminl emails.
  </p>
</section>

<section id="google-sign-in">
  <h2>6. Google Sign-In</h2>
  <p>
    If you sign in with Google, we receive your Google account email, name, and profile image as
    needed to authenticate you. We do not request Gmail, Calendar, or Contacts scopes, and we cannot
    read, send, or draft email in your Google account.
  </p>
  <p>
    You may stop using Google sign-in by using email and password instead, where available, or by
    contacting support@goliminl.com to delete your account.
  </p>
</section>

<section id="acceptable-use">
  <h2>7. Acceptable Use</h2>
  <p>You agree not to:</p>
  <ul>
    <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
    <li>Attempt to gain unauthorized access to any part of the Service, other user accounts, or any systems or networks connected to the Service</li>
    <li>Use the Service to send unsolicited or unauthorized communications, spam, or promotional materials</li>
    <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
    <li>Scrape, crawl, or use automated means to access the Service or extract data without our prior written consent</li>
    <li>Interfere with or disrupt the integrity or performance of the Service</li>
    <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
    <li>Upload or transmit viruses, malware, or other harmful code</li>
  </ul>
</section>

<section id="ai-content-disclaimer">
  <h2>8. AI-Generated Content Disclaimer</h2>
  <p>
    Liminl uses AI to generate match explanations, onboarding assistance, and email drafts. AI-generated
    content is provided for informational purposes only. Liminl Inc. does not guarantee the accuracy,
    completeness, or appropriateness of any AI-generated output. You are responsible for the information
    you submit and for how you use any suggestions.
  </p>
  <p>
    AI-generated recommendations do not constitute professional career counseling, legal advice, or any
    other form of professional guidance. Liminl may send its own matching and scheduling emails. Liminl
    does not send messages from your personal email account.
  </p>
</section>

<section id="intellectual-property">
  <h2>9. Intellectual Property</h2>
  <p>
    The Service, including its design, features, algorithms, and underlying technology, is owned by
    Liminl Inc. and is protected by intellectual property laws. These Terms do not grant you any
    right, title, or interest in the Service beyond the limited right to use it in accordance with
    these Terms.
  </p>
  <p>
    Content you submit (such as onboarding answers, resume materials, or feedback) remains yours.
    Aggregated and anonymized usage data may be used by us to improve the Service.
  </p>
</section>

<section>
  <h2>10. Third-Party Services</h2>
  <p>
    The Service uses third-party services to operate, including Google sign-in, cloud hosting, email
    delivery, AI processing, error monitoring, location lookup, and video meetings. Your use of
    those third-party services is also governed by their respective terms and privacy policies.
    Liminl Inc. is not responsible for the practices of third-party services.
  </p>
</section>

<section>
  <h2>11. Data Retention and Deletion</h2>
  <p>
    Liminl retains your account or alumni-form data for as long as needed to provide the Service.
    You may request deletion of your account and associated personal data at any time by contacting
    support@goliminl.com.
  </p>
  <p>Upon account deletion or termination:</p>
  <ul>
    <li>Your personal data will be deleted within 30 days, except where retention is required by applicable law or regulation</li>
    <li>Aggregated, anonymized data that cannot be used to identify you may be retained for analytics and service improvement purposes</li>
  </ul>
</section>

<section>
  <h2>12. Termination</h2>
  <p>
    You may stop using the Service and request deletion of your account at any time by contacting
    support@goliminl.com. We may suspend or terminate your access to the Service at our sole
    discretion, with or without notice, for conduct that we believe violates these Terms, is harmful
    to other users, or is otherwise objectionable.
  </p>
  <p>Upon termination, your right to use the Service ceases immediately. We will delete your personal data in accordance with Section 11.</p>
</section>

<section id="liability-and-warranties">
  <h2>13. Disclaimer of Warranties</h2>
  <p>
    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
    FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. LIMINL INC. DOES NOT WARRANT THAT THE SERVICE
    WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
  </p>
</section>

<section>
  <h2>14. Limitation of Liability</h2>
  <p>
    TO THE MAXIMUM EXTENT PERMITTED BY LAW, LIMINL INC. AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
    AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
    PUNITIVE DAMAGES ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE
    POSSIBILITY OF SUCH DAMAGES.
  </p>
  <p>
    LIMINL INC.'S TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE
    SHALL NOT EXCEED THE AMOUNT YOU PAID TO LIMINL INC. IN THE TWELVE (12) MONTHS PRECEDING THE
    CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
  </p>
</section>

<section>
  <h2>15. Indemnification</h2>
  <p>
    You agree to indemnify, defend, and hold harmless Liminl Inc., its officers, directors,
    employees, and agents from any claims, liabilities, damages, losses, or expenses (including
    reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms,
    or your violation of any third-party rights.
  </p>
</section>

<section>
  <h2>16. Governing Law and Dispute Resolution</h2>
  <p>
    These Terms are governed by the laws of the State of Delaware, without regard to conflict of law
    principles. Any disputes arising under these Terms shall be resolved through binding arbitration
    in accordance with the rules of the American Arbitration Association, conducted in Delaware. You
    agree to waive any right to a jury trial or to participate in a class action.
  </p>
</section>

<section>
  <h2>17. Severability</h2>
  <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
</section>

<section>
  <h2>18. Entire Agreement</h2>
  <p>
    These Terms, together with the Privacy Policy, constitute the entire agreement between you and
    Liminl Inc. regarding your use of the Service and supersede any prior agreements.
  </p>
</section>

<section id="terms-contact">
  <h2>19. Contact Information</h2>
  <p>If you have questions about these Terms, contact us at:</p>
  <p class="legal-address">
    Liminl Inc.<br />
    Email: support@goliminl.com<br />
    Web: www.goliminl.com
  </p>
</section>
`;
